import { useQueries } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { UseDataQueryOptions } from '../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../hooks/useDataQuery/useDataQuery';
import useInfiniteDataQuery from '../../../hooks/useInfiniteDataQuery/useInfiniteDataQuery';
import { InfiniteDataQueryFnUtils } from '../../../hooks/useInfiniteDataQuery/utils/useInfiniteDataQueryFn.utils';
import { useInfiniteListWithRestore } from '../../../hooks/useInfiniteListWithRestore/useInfiniteListWithRestore';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { HTTP_STATUS } from '../../../utils/httpStatus.const';
import { OmitPaginatedEndpointParams } from '../../types';
import {
  getFindBreakdownDimensionsKey,
  getFindBreakdownItemsKey,
  getInfiniteTargetsKey,
  getOrderTargetsKey,
  getTargetBreakdownsKey,
  getTargetDetailKey,
  getTargetDimensionsKey,
  getTargetMaxConversionKey,
  getTargetMethodOptionsKey,
  getTargetStatusGroupCountsKey,
  getTargetTotalsKey,
} from './target.keys';
import {
  findBreakdownDimensions,
  findBreakdownItems,
  findOrderTargets,
  findTargetBreakdowns,
  findTargetDetail,
  findTargetDimensions,
  findTargetMaxConversion,
  findTargetMethodOptions,
  findTargetStatusGroupCounts,
  findTargetTotals,
  findTargets,
  findTargetsCount,
} from './target.repository';
import {
  BreakdownItemDTO,
  FindBreakdownDimensionsOutput,
  FindBreakdownItemsQuery,
  FindOrderTargetsOutput,
  FindTargetBreakdownsOutput,
  FindTargetDetailOutput,
  FindTargetDimensionsOutput,
  FindTargetMaxConversionOutput,
  FindTargetMaxConversionParams,
  FindTargetMethodOptionsOutput,
  FindTargetMethodOptionsParams,
  FindTargetStatusGroupCountsOutput,
  FindTargetStatusGroupCountsParams,
  FindTargetTotalsOutput,
  FindTargetTotalsParams,
  FindTargetsParams,
  TargetBreakdownChildrenRequest,
} from './target.repository.types';

interface UseTargetBreakdownsByTargetIdsParams {
  targetIds: string[];
  enabledIds?: Set<string>;
  depth?: number;
}

const BREAKDOWNS_STALE_TIME = 30_000;

const BREAKDOWN_ITEMS_PAGE_SIZE = 20;
export const METHOD_OPTIONS_LIMIT = 999;

const MAX_RETRIES = 3;

const retryUnlessClientError = (failureCount: number, error: unknown) => {
  const status = (error as AxiosError)?.response?.status;

  if (status && status >= HTTP_STATUS.BAD_REQUEST && status < HTTP_STATUS.INTERNAL_SERVER_ERROR) {
    return false;
  }

  return failureCount < MAX_RETRIES;
};

export const useInfiniteTargetsList = (params: OmitPaginatedEndpointParams<FindTargetsParams>) => {
  const fetchTargetsPage = InfiniteDataQueryFnUtils.fetcherWithoutCount(
    findTargets,
    findTargetsCount,
  );

  return useInfiniteListWithRestore({
    limit: 20,
    queryKey: getInfiniteTargetsKey(params),
    defaultOrderBy: { createdAt: 'DESC' },
    queryFn: (context) => fetchTargetsPage({ ...context, params }),
  });
};

export const useOrderTargets = (orderId: string, options?: UseDataQueryOptions) =>
  useDataQuery<FindOrderTargetsOutput>(
    {
      queryKey: getOrderTargetsKey(orderId),
      queryFn: async (context) => {
        const response = await findOrderTargets(orderId, context?.signal);

        if (!response.ok || !response.data) {
          throw response.originalError ?? new Error('the order plans returned no data');
        }

        return response.data;
      },
    },
    { retry: retryUnlessClientError, ...options },
  );

export const useTargetStatusGroupCounts = (
  params: FindTargetStatusGroupCountsParams,
  options?: UseDataQueryOptions,
) =>
  useDataQuery<FindTargetStatusGroupCountsOutput>(
    {
      queryKey: getTargetStatusGroupCountsKey(params),
      queryFn: async () => {
        const response = await findTargetStatusGroupCounts(params);

        if (!response.ok || !response.data) {
          throw response.originalError ?? new Error('the plan status counts returned no data');
        }

        return response.data;
      },
    },
    options,
  );

export const useFindBreakdownDimensions = (options?: UseDataQueryOptions) =>
  useDataQuery<FindBreakdownDimensionsOutput>(
    {
      queryKey: getFindBreakdownDimensionsKey(),
      queryFn: async () => {
        const response = await findBreakdownDimensions();

        if (!response.ok || !response.data) {
          throw response.originalError;
        }

        return response.data;
      },
    },
    options,
  );

export const useFindBreakdownItems = (params: FindBreakdownItemsQuery) =>
  useInfiniteDataQuery<BreakdownItemDTO>({
    maxPages: null,
    limit: BREAKDOWN_ITEMS_PAGE_SIZE,
    defaultOrderBy: {},
    queryKey: getFindBreakdownItemsKey(params),
    queryFn: async ({ skip, limit, signal }) => {
      const response = await findBreakdownItems({ ...params, skip, limit }, signal);

      if (!response.ok || !response.data) {
        throw response.originalError;
      }

      return { data: response.data.data, countData: { total: response.data.count } };
    },
  });

export const useTargetDetail = (targetId: string) =>
  useDataQuery<FindTargetDetailOutput>(
    {
      queryKey: getTargetDetailKey(targetId),
      queryFn: async (context) => {
        const response = await findTargetDetail(targetId, context?.signal);

        if (!response.ok || !response.data) {
          throw response.originalError;
        }

        return response.data;
      },
    },
    { retry: retryUnlessClientError },
  );

export const useTargetBreakdowns = (targetId: string) =>
  useDataQuery<FindTargetBreakdownsOutput>(
    {
      queryKey: getTargetBreakdownsKey(targetId),
      queryFn: async (context) => {
        const response = await findTargetBreakdowns({ targetId, signal: context?.signal });

        if (!response.ok || !response.data) {
          throw response.originalError;
        }

        return response.data;
      },
      staleTime: BREAKDOWNS_STALE_TIME,
    },
    { retry: retryUnlessClientError },
  );

export const useTargetBreakdownsByTargetIds = ({
  targetIds,
  enabledIds,
  depth,
}: UseTargetBreakdownsByTargetIdsParams) => {
  const { team } = useApplicationContext();

  return useQueries({
    queries: targetIds.map((targetId) => ({
      queryKey: [team?.id, ...getTargetBreakdownsKey(targetId, { depth })],
      enabled: enabledIds?.has(targetId) ?? true,
      staleTime: BREAKDOWNS_STALE_TIME,
      refetchOnWindowFocus: false,
      retry: retryUnlessClientError,
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const response = await findTargetBreakdowns({ targetId, depth, signal });

        if (!response.ok || !response.data) {
          throw response.originalError;
        }

        return response.data;
      },
    })),
    combine: (results) =>
      results.map((result, index) => ({
        targetId: targetIds[index],
        items: result.data?.items,
        isLoading: result.isPending,
        isError: result.isError,
        refetch: result.refetch,
      })),
  });
};

export const useTargetBreakdownChildren = (requests: TargetBreakdownChildrenRequest[]) => {
  const { team } = useApplicationContext();

  return useQueries({
    queries: requests.map(({ targetId, parentId }) => ({
      queryKey: [team?.id, ...getTargetBreakdownsKey(targetId, { parentId })],
      staleTime: BREAKDOWNS_STALE_TIME,
      refetchOnWindowFocus: false,
      retry: retryUnlessClientError,
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const response = await findTargetBreakdowns({ targetId, parentId, signal });

        if (!response.ok || !response.data) {
          throw response.originalError;
        }

        return response.data;
      },
    })),
    combine: (results) =>
      results.map((result, index) => ({
        targetId: requests[index].targetId,
        items: result.data?.items,
        isLoading: result.isPending,
        isError: result.isError,
        refetch: result.refetch,
      })),
  });
};

export const useTargetDetailsByTargetIds = (targetIds: string[], enabledIds: Set<string>) => {
  const { team } = useApplicationContext();

  return useQueries({
    queries: targetIds.map((targetId) => ({
      queryKey: [team?.id, ...getTargetDetailKey(targetId)],
      enabled: enabledIds.has(targetId),
      refetchOnWindowFocus: false,
      retry: retryUnlessClientError,
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const response = await findTargetDetail(targetId, signal);

        if (!response.ok || !response.data) {
          throw response.originalError;
        }

        return response.data;
      },
    })),
    combine: (results) =>
      results.map((result, index) => ({
        targetId: targetIds[index],
        detail: result.data,
        refetch: result.refetch,
      })),
  });
};

export const useTargetTotals = (params: FindTargetTotalsParams) =>
  useDataQuery<FindTargetTotalsOutput>({
    queryKey: getTargetTotalsKey(params),
    queryFn: async () => {
      const response = await findTargetTotals(params);

      if (!response.ok || !response.data) {
        throw response.originalError;
      }

      return response.data;
    },
  });

export const useTargetMaxConversion = (params: FindTargetMaxConversionParams) =>
  useDataQuery<FindTargetMaxConversionOutput>({
    queryKey: getTargetMaxConversionKey(params),
    queryFn: async () => {
      const response = await findTargetMaxConversion(params);

      if (!response.ok || !response.data) {
        throw response.originalError;
      }

      return response.data;
    },
  });

export const useTargetMethodOptions = (
  { type, active }: Pick<FindTargetMethodOptionsParams, 'type' | 'active'>,
  options?: UseDataQueryOptions<FindTargetMethodOptionsOutput>,
) => {
  const params = { type, active, skip: 0, limit: METHOD_OPTIONS_LIMIT };

  return useDataQuery<FindTargetMethodOptionsOutput>(
    {
      queryKey: getTargetMethodOptionsKey(params),
      queryFn: async () => {
        const response = await findTargetMethodOptions(params);

        if (!response.ok || !response.data) {
          throw response.originalError;
        }

        return response.data;
      },
    },
    options,
  );
};

export const useTargetDimensions = (
  targetId: string,
  options?: UseDataQueryOptions<FindTargetDimensionsOutput>,
) =>
  useDataQuery<FindTargetDimensionsOutput>(
    {
      queryKey: getTargetDimensionsKey(targetId),
      queryFn: async (context) => {
        const response = await findTargetDimensions(targetId, context?.signal);

        if (!response.ok || !response.data) {
          throw response.originalError ?? new Error('the plan dimensions returned no data');
        }

        return response.data;
      },
    },
    options,
  );
